/* src/components/admin/announcements/dashboard/AnnouncementPostView.js */
import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../../firebaseConfig";
import {
  addComment,
  deleteAnnouncement,
  deleteComment,
  setPinned,
  subscribeComments,
  subscribeReadsList,
  toggleArchive,
} from "../AdminAnnouncementsLogic";
import ReadReceiptsModal from "./ReadReceiptsModal";
import ToastHost from "./ToastHost";

import {
  FiArrowLeft,
  FiEdit2,
  FiTrash2,
  FiMessageSquare,
  FiTag,
  FiUser,
  FiClock,
  FiCornerDownRight,
  FiArchive,
  FiUsers,
  FiEye,
} from "react-icons/fi";
import { BsPinAngle, BsPinFill } from "react-icons/bs";
import "./AnnouncementPostView.css";

function fmtDate(ts) {
  const ms = ts?.toMillis?.() ?? null;
  if (!ms) return "";
  const d = new Date(ms);
  return (
    d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) +
    " at " +
    d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
  );
}

export default function AnnouncementPostView({ me, announcementId, onBack, onEdit }) {
  const [a, setA] = useState(null);
  const [comments, setComments] = useState([]);
  const [reads, setReads] = useState([]);
  const [text, setText] = useState("");
  const [toast, setToast] = useState(null);
  const [readsModal, setReadsModal] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "announcements", announcementId), (snap) => {
      setA(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return () => unsub?.();
  }, [announcementId]);

  useEffect(() => {
    const unsub = subscribeComments(announcementId, (rows) => setComments(rows || []));
    return () => unsub?.();
  }, [announcementId]);

  useEffect(() => {
    const unsub = subscribeReadsList(announcementId, (rows) => setReads(rows || []));
    return () => unsub?.();
  }, [announcementId]);

  const audienceLabel = useMemo(() => {
    if (!a) return "";
    return a.audienceType === "all"
      ? "All Tutors"
      : `${(a.audienceUserIds || []).length} Tutors`;
  }, [a]);

  const handlePinToggle = async () => {
    if (!a) return;
    try {
      await setPinned(announcementId, !a.pinned);
      setToast({ kind: "success", msg: !a.pinned ? "Pinned." : "Unpinned." });
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Failed." });
    }
  };

  const handleArchiveToggle = async () => {
    if (!a) return;
    try {
      await toggleArchive(announcementId, a.archived);
      setToast({ kind: "success", msg: a.archived ? "Restored." : "Archived." });
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Failed." });
    }
  };

  const handleDeletePost = async () => {
    if (!a) return;
    if (!window.confirm("Delete this announcement? This cannot be undone.")) return;
    try {
      await deleteAnnouncement(announcementId);
      onBack?.();
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Delete failed." });
    }
  };

  const submitComment = async () => {
    if (!a) return;
    if (a.commentsEnabled === false) {
      setToast({ kind: "info", msg: "Comments are disabled." });
      return;
    }
    if (!text.trim()) return;
    try {
      await addComment(announcementId, text);
      setText("");
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Failed to comment." });
    }
  };

  const handleDeleteComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await deleteComment(announcementId, cid);
    } catch (e) {
      setToast({ kind: "error", msg: e?.message || "Failed." });
    }
  };

  if (!a) {
    return (
      <div className="aaPost">
        <div className="aaPostContainer">
          <button className="aaPostBackBtn" onClick={onBack} type="button">
            <FiArrowLeft size={15} /> Announcements
          </button>
          <div className="aaPostMissing">This post no longer exists.</div>
        </div>
        <ToastHost toast={toast} onClear={() => setToast(null)} />
      </div>
    );
  }

  return (
    <div className="aaPost">
      <div className="aaPostContainer">

        {/* ── Back navigation only ── */}
        <button className="aaPostBackBtn" onClick={onBack} type="button">
          <FiArrowLeft size={15} /> Announcements
        </button>

        {/* ── Post card ── */}
        <div className="aaPostCard">

          {/* Card top: badges (left) + action buttons (right) */}
          <div className="aaPostCardTop">
            <div className="aaPostBadgesRow">
              {a.pinned && (
                <span className="aaPostBadge">
                  <BsPinFill size={9} /> Pinned
                </span>
              )}
              {a.archived && (
                <span className="aaPostBadge muted">Archived</span>
              )}
              <span className="aaPostCatBadge">
                <FiTag size={9} /> {(a.category || "General").trim() || "General"}
              </span>
            </div>

            <div className="aaPostCardActions">
              <button
                className="aaPostActionBtn"
                onClick={handlePinToggle}
                type="button"
                title={a.pinned ? "Unpin" : "Pin"}
              >
                {a.pinned ? <BsPinFill size={11} /> : <BsPinAngle size={11} />}
                {a.pinned ? "Unpin" : "Pin"}
              </button>
              <button
                className="aaPostActionBtn"
                onClick={() => onEdit?.(announcementId)}
                type="button"
              >
                <FiEdit2 size={11} /> Edit
              </button>
              <button className="aaPostActionBtn" onClick={handleArchiveToggle} type="button">
                <FiArchive size={11} /> {a.archived ? "Restore" : "Archive"}
              </button>
              <button
                className="aaPostActionBtn reads"
                onClick={() => setReadsModal(true)}
                type="button"
                title="View read receipts"
              >
                <FiEye size={11} /> Read: {reads.length}
              </button>
              <button
                className="aaPostActionBtn danger"
                onClick={handleDeletePost}
                type="button"
                title="Delete"
              >
                <FiTrash2 size={11} />
              </button>
            </div>
          </div>

          <h1 className="aaPostMainTitle">{a.title || "(Untitled)"}</h1>

          <div className="aaPostMetaRow">
            <span className="aaPostMetaItem">
              <FiUser size={12} /> {a.createdByName || "Admin"}
            </span>
            <span className="aaPostMetaDot">·</span>
            <span className="aaPostMetaItem">
              <FiClock size={12} /> {fmtDate(a.createdAt)}
            </span>
            <span className="aaPostMetaDot">·</span>
            <span className="aaPostMetaItem">
              <FiUsers size={12} /> {audienceLabel}
            </span>
            <span className="aaPostMetaDot">·</span>
            <span className="aaPostMetaItem">
              <FiMessageSquare size={12} /> {comments.length} comment{comments.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="aaPostDivider" />

          <div className="aaPostContent" dangerouslySetInnerHTML={{ __html: a.contentHtml || "" }} />
        </div>

        {/* ── Comments card ── */}
        <div className="aaCommentsCard" id="aaCommentBox">
          <div className="aaCommentsHead">
            <FiMessageSquare size={15} />
            <span>Comments</span>
            {comments.length > 0 && (
              <span className="aaCommentsCount">{comments.length}</span>
            )}
          </div>

          {a.commentsEnabled === false ? (
            <div className="aaCommentsDisabled">Comments are disabled for this post.</div>
          ) : (
            <div className="aaCommentComposer">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write a comment…"
                rows={3}
              />
              <div className="aaCommentComposerFoot">
                <button
                  className="aaPostBtn accent"
                  onClick={submitComment}
                  type="button"
                  disabled={!text.trim()}
                >
                  <FiCornerDownRight size={13} /> Post Comment
                </button>
              </div>
            </div>
          )}

          {comments.length > 0 && (
            <div className="aaCommentsList">
              {comments.map((c) => {
                const mine = c.uid && me?.uid && c.uid === me.uid;
                return (
                  <div key={c.id} className="aaComment">
                    <div className="aaCommentAvatar">{(c.userName || "U").slice(0, 1).toUpperCase()}</div>
                    <div className="aaCommentBody">
                      <div className="aaCommentTop">
                        <span className="aaCommentName">{c.userName || "User"}</span>
                        {mine && (
                          <button
                            className="aaCommentDelete"
                            onClick={() => handleDeleteComment(c.id)}
                            type="button"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      <div className="aaCommentText">{c.text || ""}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {comments.length === 0 && a.commentsEnabled !== false && (
            <div className="aaCommentsEmpty">No comments yet. Be the first to start the discussion!</div>
          )}
        </div>
      </div>

      {readsModal && (
        <ReadReceiptsModal
          announcement={{ id: a.id, title: a.title }}
          onClose={() => setReadsModal(false)}
        />
      )}
      <ToastHost toast={toast} onClear={() => setToast(null)} />
    </div>
  );
}
